import Icon from "@booking/components/atoms/media/Icon";
import { ButtonActionEnum, IconEnum, SizeEnum } from "@booking/enums";
import { renderChildren } from "@booking/utils/helpers";
import { IconDefinition } from "@fortawesome/free-solid-svg-icons";
import clsx from "clsx";
import React from "react";

import { type CommonProps } from "@/types";
import { createId } from "@/utils/helpers";

import styles from "./Button.module.css";

const ref = "button";

enum ButtonVariantEnum {
  IMAGE = "image",
  OUTLINE = "outline",
  PLAIN = "plain",
  SOLID = "solid",
}

interface ButtonProps extends Partial<CommonProps> {
  active?: boolean;
  children?: React.ReactNode;
  disabled?: boolean;
  formMethod?: string;
  href?: string;
  icon?: IconDefinition | IconEnum;
  rounded?: boolean;
  size?: SizeEnum;
  title?: string;
  target?: string;
  type?: ButtonActionEnum;
  variant?: ButtonVariantEnum;
  onClick?: React.MouseEventHandler<HTMLAnchorElement | HTMLButtonElement>;
}

function useButton(props: ButtonProps) {
  const {
    active,
    color,
    disabled,
    href,
    icon,
    rounded,
    title,
    type,
    variant = ButtonVariantEnum.SOLID,
    ...rest
  } = props;

  return {
    active,
    children: props.children,
    color,
    disabled: !!disabled,
    href,
    icon,
    rounded,
    title,
    type: type || href ? undefined : ButtonActionEnum.BUTTON,
    variant,
    ...rest,
  };
}

function Button(props: ButtonProps) {
  const {
    active,
    children,
    color,
    disabled,
    href,
    icon,
    rounded,
    title,
    type,
    variant,
    ...rest
  } = useButton(props);
  const className = clsx(
    styles[ref],
    color && styles[color],
    variant && styles[variant],
    active && styles.active,
    disabled && styles.disabled,
    rounded && styles.rounded,
    props.size && props.size !== SizeEnum.STANDARD && styles[props.size],
    href && styles.link,
    props.className,
  );

  if (!children && !icon) {
    return null;
  }

  const id = createId(ref);

  return href ? (
    <a
      {...rest}
      className={className}
      href={href}
      role={"button"}
      title={title}
      data-type="button"
    >
      {icon && <Icon name={icon} noPadding />}

      {renderChildren(id, children)}
    </a>
  ) : (
    <button
      {...rest}
      className={className}
      disabled={disabled}
      title={title}
      type={type}
    >
      {variant !== ButtonVariantEnum.IMAGE && icon && (
        <Icon name={icon} noPadding />
      )}
      {renderChildren(id, children)}
    </button>
  );
}

export default Button;
export { Button, type ButtonProps, ButtonVariantEnum };
